import React, { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "../../../../utils/appToast";
import { nextDocId } from "../../constants";
import { getTemplate, ingestDocument, universalExtract, updateTemplate, patchTemplate } from "../../extractorApi";
import OcrEngineModal from "../../OcrEngineModal";
import {
  errMsg,
  isSupportedStudioTestFile,
  normalizeTemplateSlug,
  serializeTemplateFields,
} from "../studioConst";
import TemplateDetail from "./TemplateDetail";
import TestWorkspace from "./TestWorkspace";

const stripStats = (results) => {
  const { _stats, ...rest } = results || {};
  return rest;
};

const templateConfig = (template) => ({
  engine: template.extraction_engine || template.engine || "standard_layout",
  retrieval: template.retrieval_strategy || template.retrieval || "full_text",
  model: template.llm_model_key || template.model || "gemini_2_5_flash_lite",
});

const extractionData = (response) =>
  response?.extracted_data ?? response?.data ?? response?.result ?? response;

/* Saved-template workspace.
   The database template is the source of truth. Local state is only an editing
   buffer; Save replaces it with a fresh backend response before testing can run. */
const Workspace = ({ template: initialTemplate, onBack, onSaved }) => {
  const [template, setTemplate] = useState(initialTemplate);
  const [templateName, setTemplateName] = useState(initialTemplate.name || "");
  const [slugDraft, setSlugDraft] = useState(
    initialTemplate.slug || normalizeTemplateSlug(initialTemplate.name)
  );
  const [screen, setScreen] = useState("detail");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ocrModalOpen, setOcrModalOpen] = useState(false);
  const [config, setConfig] = useState(templateConfig(initialTemplate));

  const [docs, setDocs] = useState([]);
  const [activeDocId, setActiveDocId] = useState(null);
  const [testing, setTesting] = useState(false);
  const unmountedRef = useRef(false);

  // Always re-read on entry so this workspace reflects the saved template.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        let fresh = await getTemplate(initialTemplate.id);
        if (!fresh.slug) {
          const fallbackSlug = normalizeTemplateSlug(fresh.name) || `template-${Date.now()}`;
          const patched = await patchTemplate(fresh.id, { slug: fallbackSlug });
          fresh = { ...fresh, ...patched, slug: patched.slug || fallbackSlug };
        }
        if (cancelled) return;
        setTemplate(fresh);
        setTemplateName(fresh.name || "");
        setSlugDraft(fresh.slug || normalizeTemplateSlug(fresh.name));
        setConfig(templateConfig(fresh));
      } catch (err) {
        if (!cancelled) toast.error(errMsg(err, "Couldn't load the saved template."));
      }
    };
    refresh();
    return () => { cancelled = true; };
  }, [initialTemplate.id]);

  useEffect(() => () => { unmountedRef.current = true; }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  const fields = template.fields || [];
  const slug = slugDraft || template.slug || normalizeTemplateSlug(templateName);
  const markDirty = () => setDirty(true);

  const handleBackWithWarning = () => {
    if (dirty && !window.confirm("You have unsaved changes. Are you sure you want to leave without saving?")) return;
    onBack?.();
  };

  const updateDoc = useCallback((id, updates) => {
    setDocs((prev) => prev.map((doc) => (doc.id === id ? { ...doc, ...updates } : doc)));
  }, []);

  const updateField = (fieldKey, updater) => {
    setTemplate((current) => ({
      ...current,
      fields: (current.fields || []).map((field) =>
        field.field_key === fieldKey ? updater(field) : field
      ),
    }));
    markDirty();
  };

  const handleToggleEnable = (fieldKey, enabled) => updateField(fieldKey, (field) => ({ ...field, enabled }));

  const handleRename = (fieldKey, newKey) => {
    const nextKey = newKey.trim();
    if (!nextKey) return;
    if (fields.some((field) => field.field_key === nextKey && field.field_key !== fieldKey)) {
      toast.error("A field with that key already exists.");
      return;
    }
    updateField(fieldKey, (field) => ({ ...field, field_key: nextKey }));
  };

  const handleChangeType = (fieldKey, dataType) => updateField(fieldKey, (field) => {
    let schema_definition = field.schema_definition;
    if (dataType === "object" && field.schema_definition?.type !== "object") {
      schema_definition = { type: "object", properties: [] };
    } else if (dataType === "table" && field.schema_definition?.type !== "table") {
      schema_definition = { type: "table", items: { type: "object", properties: [] } };
    } else if (dataType !== "object" && dataType !== "table") {
      schema_definition = null;
    }
    return { ...field, data_type: dataType, schema_definition };
  });

  const handleChangeSchema = (fieldKey, schema_definition) =>
    updateField(fieldKey, (field) => ({ ...field, schema_definition }));

  const handleChangeInstruction = (fieldKey, text, isRules) =>
    updateField(fieldKey, (field) => ({
      ...field,
      ...(isRules ? { output_rules: text } : { instruction: text }),
    }));

  const handleDelete = (fieldKey) => {
    setTemplate((current) => ({
      ...current,
      fields: (current.fields || []).filter((field) => field.field_key !== fieldKey),
    }));
    markDirty();
  };

  const handleAddField = () => {
    const existing = new Set(fields.map((field) => field.field_key));
    let n = fields.length + 1;
    let key = `new_field_${n}`;
    while (existing.has(key)) { n += 1; key = `new_field_${n}`; }
    setTemplate((current) => ({
      ...current,
      fields: [...(current.fields || []), {
        field_key: key,
        data_type: "text",
        instruction: "",
        confidence: "manual",
        enabled: true,
      }],
    }));
    markDirty();
  };

  // Test documents are local to this session; the saved template is not.
  const handleAddFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter(isSupportedStudioTestFile);
    if (!files.length) {
      if (fileList?.length) toast.error("Supported files: PDF, DOCX, TXT, XLSX, XLS, RTF, JPG, JPEG and PNG.");
      return;
    }
    const newDocs = files.map((file) => ({
      id: nextDocId(), file, name: file.name, on: true,
      ingesting: true, ingestData: null, extracting: false, result: null,
    }));
    setDocs((prev) => [...prev, ...newDocs]);
    setActiveDocId((prev) => prev || newDocs[0].id);

    for (const doc of newDocs) {
      try {
        const data = await ingestDocument(doc.file, { engine: config.engine });
        if (!unmountedRef.current) updateDoc(doc.id, { ingestData: data, ingesting: false });
      } catch (err) {
        if (!unmountedRef.current) {
          updateDoc(doc.id, { ingesting: false, ingestData: null });
          toast.error(errMsg(err, `Couldn't ingest ${doc.name}.`));
        }
      }
    }
  }, [config.engine, updateDoc]);

  const handleRemoveDoc = (id) => {
    setDocs((prev) => {
      const next = prev.filter((doc) => doc.id !== id);
      setActiveDocId((active) => active === id ? (next[0]?.id || null) : active);
      return next;
    });
  };

  const handleToggleDoc = (id) =>
    setDocs((prev) => prev.map((doc) => (doc.id === id ? { ...doc, on: !doc.on } : doc)));

  const handleRunExtraction = async () => {
    if (dirty) {
      toast.error("Save the template before running extraction.");
      return;
    }
    const selected = docs.filter((doc) => doc.on && doc.ingestData && !doc.ingesting);
    if (!selected.length) {
      toast.error(docs.length ? "Wait for ingestion to finish, then run." : "Upload a document first.");
      return;
    }
    if (!template.slug) {
      toast.error("This template has no saved slug. Save the template before testing.");
      return;
    }

    setScreen("test");
    setTesting(true);
    selected.forEach((doc) => updateDoc(doc.id, { extracting: true, result: null, stage: "extracting" }));
    let completed = 0;
    let failed = 0;

    try {
      for (const doc of selected) {
        if (unmountedRef.current) break;
        try {
          const response = await universalExtract(doc.file, template.slug);
          if (unmountedRef.current) break;
          updateDoc(doc.id, {
            extracting: false,
            stage: null,
            result: stripStats(extractionData(response)),
          });
          completed += 1;
        } catch (err) {
          if (unmountedRef.current) break;
          failed += 1;
          updateDoc(doc.id, {
            extracting: false,
            stage: null,
            result: { _error: errMsg(err, "Extraction failed.") },
          });
        }
      }
      if (!unmountedRef.current) {
        if (completed && !failed) toast.success("Extraction complete.");
        else if (completed && failed) toast.error(`${completed} document${completed === 1 ? "" : "s"} extracted; ${failed} failed.`);
        else if (failed) toast.error("Extraction failed. Check the template and try again.");
      }
    } finally {
      if (!unmountedRef.current) setTesting(false);
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig((current) => ({ ...current, [key]: value }));
    markDirty();
  };

  const handleExportJson = () => {
    const payload = {
      name: templateName || "template",
      document_type: template.document_type,
      fields: serializeTemplateFields(fields),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(templateName || "template").replace(/[^a-z0-9]/gi, "_")}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    if (!templateName.trim()) { toast.error("Give the template a name first."); return; }
    if (!fields.some((field) => field.enabled !== false)) { toast.error("Enable at least one field before saving."); return; }
    const nextSlug = normalizeTemplateSlug(slugDraft || templateName);
    if (nextSlug.length < 3) { toast.error("The template slug must contain at least 3 letters or numbers."); return; }
    const persistedFields = serializeTemplateFields(fields);
    if (!persistedFields.length) { toast.error("Enable at least one field before saving."); return; }

    setSaving(true);
    try {
      await updateTemplate(template.id, {
        name: templateName.trim(),
        slug: nextSlug,
        document_type: template.document_type === "auto" ? "custom" : template.document_type,
        extraction_engine: config.engine,
        retrieval_strategy: config.retrieval,
        llm_model_key: config.model,
        fields: persistedFields,
      });
      const saved = await getTemplate(template.id);
      setTemplate(saved);
      setTemplateName(saved.name || "");
      setSlugDraft(saved.slug || nextSlug);
      setConfig(templateConfig(saved));
      setDirty(false);
      setDocs((prev) => prev.map((doc) => ({ ...doc, extracting: false, stage: null, result: null })));
      toast.success("Template saved.");
      onSaved?.(saved, { updated: true });
    } catch (err) {
      if (err?.response?.status === 404) toast.error("This template no longer exists. Return to the library and reopen it.");
      else toast.error(errMsg(err, "Failed to save template."));
    } finally {
      setSaving(false);
    }
  };

  const shared = {
    template,
    templateName,
    slug,
    config,
    onConfigChange: handleConfigChange,
    onAddField: handleAddField,
    onRename: handleRename,
    onChangeType: handleChangeType,
    onChangeInstruction: handleChangeInstruction,
    onChangeSchema: handleChangeSchema,
    onDelete: handleDelete,
    onToggleEnable: handleToggleEnable,
    dirty,
    saving,
  };

  return (
    <>
      <OcrEngineModal
        isOpen={ocrModalOpen}
        onClose={() => setOcrModalOpen(false)}
        currentEngine={config.engine}
        onSelect={(value) => { setOcrModalOpen(false); handleConfigChange("engine", value); }}
      />
      {screen === "detail" ? (
        <TemplateDetail
          {...shared}
          onNameChange={(value) => { setTemplateName(value); markDirty(); }}
          onSlugChange={(value) => { setSlugDraft(value); markDirty(); }}
          onSave={handleSave}
          onStartTest={() => setScreen("test")}
          onBack={handleBackWithWarning}
        />
      ) : (
        <TestWorkspace
          {...shared}
          onOpenEngineModal={() => setOcrModalOpen(true)}
          onExportJson={handleExportJson}
          docs={docs}
          activeDocId={activeDocId}
          onSetActive={setActiveDocId}
          onAddFiles={handleAddFiles}
          onRemoveDoc={handleRemoveDoc}
          onToggleDoc={handleToggleDoc}
          onRunExtraction={handleRunExtraction}
          testing={testing}
          onSaveToTemplate={handleSave}
          onBack={() => { if (!dirty || window.confirm("You have unsaved changes. Are you sure you want to go back without saving?")) setScreen("detail"); }}
        />
      )}
    </>
  );
};

export default Workspace;
